import { ASSESSMENT_METHODOLOGY_VERSION, RATING_SCALE } from '../../config/assessmentConfig'
import { carbonSourcesForSector } from '../../data/assessment/carbonSources'
import { coreTopicsForSector, topicsForSector } from '../../data/assessment/topics'
import { evaluateApplicability } from './applicability'
import {
  averageScores,
  confidenceFromScores,
  evidenceConfidenceLabel,
  ratingToScore,
  readinessToScore,
  scoreToPercent,
} from './confidence'
import { topNTopics, rankTopics } from './prioritization'
import { buildRecommendations } from './recommendations'
import type {
  AssessmentResult,
  AssessmentState,
  CompanyProfile,
  EvidenceGap,
  LeadQualification,
  RatingValue,
  ScopeReadinessSummary,
  TopicResult,
} from './types'
import { countNotSure } from './validation'

export function createEmptyProfile(): CompanyProfile {
  return {
    companyName: '',
    sector: '',
    emirate: '',
    companySize: '',
    businessActivities: [],
    operatingFootprint: '',
    listingStatus: '',
    facilityCount: '',
    revenueBand: '',
    employeeCountApprox: '',
    mainlandOrFreezone: '',
    exportMarkets: [],
    hasReportingObligations: undefined,
    customersRequestEsg: undefined,
  }
}

export function createInitialState(): AssessmentState {
  return {
    methodologyVersion: ASSESSMENT_METHODOLOGY_VERSION,
    currentStep: 'company',
    companyProfile: createEmptyProfile(),
    responses: {},
    materiality: {},
    carbon: {},
    showExtendedTopics: false,
    additionalTopics: [],
    completed: false,
  }
}

function ratingLabel(value: RatingValue | undefined): string {
  if (value == null) return 'Not rated'
  if (value === 'not_sure') return 'Not sure'
  if (value === 'not_applicable') return 'Not applicable'
  return RATING_SCALE[value].label
}

function carbonSourceScore(entry: AssessmentState['carbon'][string] | undefined): number | null {
  if (!entry) return null
  if (entry.exists === 'no') return null // not applicable to score
  if (entry.exists === 'not_sure') return null
  if (entry.exists !== 'yes') return null

  const parts = [
    entry.dataCollected === 'yes' ? 1 : entry.dataCollected === 'no' ? 0.15 : entry.dataCollected === 'not_sure' ? null : null,
    entry.evidenceAvailable === 'yes' ? 1 : entry.evidenceAvailable === 'no' ? 0.15 : entry.evidenceAvailable === 'not_sure' ? null : null,
    entry.ownerAssigned === 'yes' ? 1 : entry.ownerAssigned === 'no' ? 0.2 : entry.ownerAssigned === 'not_sure' ? null : null,
    entry.methodDocumented === 'yes' ? 1 : entry.methodDocumented === 'no' ? 0.15 : entry.methodDocumented === 'not_sure' ? null : null,
    readinessToScore(entry.readiness),
  ]

  const coverageBonus =
    entry.coverage === 'all' ? 1 : entry.coverage === 'most' ? 0.75 : entry.coverage === 'some' ? 0.4 : entry.coverage ? 0.25 : null

  return averageScores([...parts, coverageBonus])
}

function buildTopicResults(state: AssessmentState): TopicResult[] {
  const topics = topicsForSector(state.companyProfile.sector || 'other', {
    includeExtended: Boolean(state.showExtendedTopics),
  })
  // Always include rated extended topics even if toggle later turned off
  const ratedIds = new Set(Object.keys(state.materiality))
  const library = topicsForSector(state.companyProfile.sector || 'other', { includeExtended: true })
  const merged = [...topics]
  for (const topic of library) {
    if (ratedIds.has(topic.id) && !merged.some((t) => t.id === topic.id)) merged.push(topic)
  }

  const results: TopicResult[] = merged.map((topic) => {
    const rating = state.materiality[topic.id]
    const financialScore = ratingToScore(rating?.financial)
    const impactScore = ratingToScore(rating?.impact)
    const missingEvidence: string[] = []
    if (financialScore == null) missingEvidence.push('Financial materiality rating incomplete or uncertain')
    if (impactScore == null) missingEvidence.push('Impact materiality rating incomplete or uncertain')
    if ((financialScore ?? 0) >= 4 || (impactScore ?? 0) >= 4) {
      missingEvidence.push(...topic.evidencePrompts.slice(0, 2))
    }

    const answeredDims = [financialScore, impactScore].filter((v) => v != null).length
    const notSureDims =
      (rating?.financial === 'not_sure' ? 1 : 0) + (rating?.impact === 'not_sure' ? 1 : 0)

    return {
      topicId: topic.id,
      name: topic.name,
      pillar: topic.pillar,
      financialScore,
      impactScore,
      financialLabel: ratingLabel(rating?.financial),
      impactLabel: ratingLabel(rating?.impact),
      priorityRank: 0,
      confidence: confidenceFromScores(answeredDims, 2, notSureDims),
      missingEvidence,
      recommendedAction:
        (financialScore ?? 0) >= 4 || (impactScore ?? 0) >= 4
          ? `Prioritize evidence gathering and ownership for ${topic.name}.`
          : undefined,
    }
  })

  for (const extra of state.additionalTopics || []) {
    if (!extra.name?.trim()) continue
    results.push({
      topicId: extra.id,
      name: extra.name.trim(),
      pillar: 'governance',
      financialScore: ratingToScore(extra.financial),
      impactScore: ratingToScore(extra.impact),
      financialLabel: ratingLabel(extra.financial),
      impactLabel: ratingLabel(extra.impact),
      priorityRank: 0,
      confidence: 'low',
      missingEvidence: ['User-identified topic: validate definition, evidence and stakeholder relevance'],
      recommendedAction: `Define scope and evidence owners for additional topic “${extra.name.trim()}”.`,
    })
  }

  return rankTopics(results)
}

/** Exported for wizard validation. */
export function requiredMaterialityTopics(state: AssessmentState) {
  return coreTopicsForSector(state.companyProfile.sector || 'other')
}

function buildCarbonByScope(state: AssessmentState): ScopeReadinessSummary[] {
  const sources = carbonSourcesForSector(state.companyProfile.sector)
  return (['1', '2', '3'] as const).map((scope) => {
    const scoped = sources.filter((s) => s.scope === scope)
    const scores: Array<number | null> = []
    const gaps: string[] = []
    let answered = 0
    let applicable = 0

    for (const source of scoped) {
      const entry = state.carbon[source.id]
      if (!entry || entry.exists == null) {
        applicable += 1
        gaps.push(`${source.label}: not assessed`)
        continue
      }
      if (entry.exists === 'no') {
        // excluded from scoring
        answered += 1
        continue
      }
      applicable += 1
      if (entry.exists === 'not_sure') {
        gaps.push(`${source.label}: existence uncertain; not treated as zero emissions`)
        continue
      }
      answered += 1
      const score = carbonSourceScore(entry)
      scores.push(score)
      if (entry.dataCollected === 'no') {
        gaps.push(`${source.label}: activity data not collected (missing data ≠ zero emissions)`)
      } else if (score != null && score < 0.5) {
        gaps.push(`${source.label}: low readiness`)
      }
    }

    return {
      scope,
      applicableCount: applicable,
      answeredCount: answered,
      unansweredCount: Math.max(0, applicable - answered),
      scorePercent: scoreToPercent(averageScores(scores)),
      gaps,
    }
  })
}

function buildEvidenceGaps(state: AssessmentState, carbonByScope: ScopeReadinessSummary[]): EvidenceGap[] {
  const gaps: EvidenceGap[] = []

  for (const scope of carbonByScope) {
    for (const gap of scope.gaps.slice(0, 4)) {
      gaps.push({
        id: `carbon_${scope.scope}_${gaps.length}`,
        area: `Scope ${scope.scope}`,
        description: gap,
        severity: gap.includes('not collected') || gap.includes('not assessed') ? 'high' : 'medium',
      })
    }
  }

  const evidenceChecks: Array<[string, string]> = [
    ['ev_source_documents', 'Source document support for activity data'],
    ['ev_methods_documented', 'Documented calculation methods'],
    ['ev_emission_factors', 'Emission-factor source and version register'],
    ['ev_boundary', 'Defined reporting boundaries'],
    ['ev_review', 'Review before reporting'],
    ['ev_owners', 'Assigned data owners'],
  ]

  for (const [id, label] of evidenceChecks) {
    const value = state.responses[id]
    if (value === 'not_implemented' || value === 'not_sure') {
      gaps.push({
        id,
        area: 'Evidence & governance',
        description: `${label} is not implemented or uncertain.`,
        severity: id === 'ev_boundary' ? 'high' : 'medium',
      })
    } else if (value === 'partial' || value === 'implemented_informal') {
      gaps.push({
        id: `${id}_partial`,
        area: 'Evidence & governance',
        description: `${label} is only partially or informally in place.`,
        severity: 'medium',
      })
    }
  }

  return gaps.slice(0, 20)
}

function buildLeadQualification(state: AssessmentState, result: Omit<AssessmentResult, 'leadQualification'>): LeadQualification {
  const signals: string[] = []
  const footprint = state.companyProfile.operatingFootprint
  if (footprint === 'multi_site_same_emirate' || footprint === 'multi_emirate' || footprint === 'uae_and_international') {
    signals.push('Multiple facilities or multi-jurisdiction footprint')
  }
  if (result.evidenceGaps.some((g) => g.severity === 'high')) {
    signals.push('Material data or evidence gaps')
  }
  if (result.carbonByScope.find((s) => s.scope === '3' && s.gaps.length >= 2)) {
    signals.push('Scope 3 screening needs')
  }
  if (
    state.responses.rc_customer_requests === 'yes' ||
    state.responses.st_customers === 'frequent' ||
    state.responses.st_customers === 'occasional' ||
    state.responses.st_investors === 'yes' ||
    state.responses.st_lenders === 'yes'
  ) {
    signals.push('Customer, lender or investor reporting requests')
  }
  if (state.responses.ev_source_documents !== 'implemented_documented') {
    signals.push('Need for centralized evidence')
  }
  if (state.responses.ev_methods_documented !== 'implemented_documented') {
    signals.push('Need for calculation traceability')
  }
  if (state.responses.rc_has_ghg_inventory === 'yes') {
    signals.push('Existing GHG reporting process')
  }

  let scoreBand: LeadQualification['scoreBand'] = 'exploratory'
  if (signals.length >= 5) scoreBand = 'strong_need'
  else if (signals.length >= 2) scoreBand = 'emerging_need'

  return {
    signals,
    scoreBand,
    notes:
      'Qualification uses business-process signals only. A low readiness score alone does not qualify a lead — small or simple businesses may score low without needing enterprise software.',
  }
}

export function computeAssessmentResult(state: AssessmentState): AssessmentResult {
  const topicResults = buildTopicResults(state)
  const topPriorityTopics = topNTopics(topicResults, 5)
  const carbonByScope = buildCarbonByScope(state)
  const evidenceGaps = buildEvidenceGaps(state, carbonByScope)

  const evidenceScores = [
    'ev_source_documents',
    'ev_consistent_collection',
    'ev_methods_documented',
    'ev_emission_factors',
    'ev_review',
    'ev_estimates',
    'ev_boundary',
    'ev_exclusions',
    'ev_owners',
    'ev_retrieval',
  ].map((id) => readinessToScore(state.responses[id] as never))

  const evidenceAvg = averageScores(evidenceScores)
  const evidenceConfidencePercent = scoreToPercent(evidenceAvg)
  const carbonAvg = averageScores(carbonByScope.map((s) => (s.scorePercent == null ? null : s.scorePercent / 100)))
  const overallReadinessPercent = scoreToPercent(averageScores([evidenceAvg, carbonAvg]))

  const unansweredMaterialityCount = topicResults.filter(
    (t) => t.financialScore == null && t.impactScore == null,
  ).length
  const notSureCount = countNotSure(state)
  const recommendations = buildRecommendations(state, topPriorityTopics, evidenceGaps)
  const applicability = evaluateApplicability(state)

  const companyLabel = state.companyProfile.companyName?.trim() || 'Your company'
  const summary = [
    `${companyLabel} completed a preliminary UAE sustainability and GHG readiness self-assessment (methodology v${ASSESSMENT_METHODOLOGY_VERSION}).`,
    overallReadinessPercent != null
      ? `Combined carbon-and-evidence readiness is approximately ${overallReadinessPercent}% based on applicable answered items.`
      : 'Combined readiness could not be scored because too many applicable items were unanswered or marked not sure.',
    `Top priority topics include ${topPriorityTopics.map((t) => t.name).slice(0, 3).join('; ') || 'topics still awaiting ratings'}.`,
    'Results are not a legal compliance determination, assurance opinion or verified inventory.',
  ].join(' ')

  const partial: Omit<AssessmentResult, 'leadQualification'> = {
    methodologyVersion: ASSESSMENT_METHODOLOGY_VERSION,
    generatedAt: new Date().toISOString(),
    companyProfile: state.companyProfile,
    summary,
    overallReadinessPercent,
    overallReadinessExplanation:
      'Overall readiness blends Scope 1 to 3 process readiness (applicable sources only) with self-reported evidence/governance maturity. It does not replace the materiality matrix or regulatory applicability panel.',
    topicResults,
    topPriorityTopics,
    carbonByScope,
    evidenceGaps,
    evidenceConfidenceLabel: evidenceConfidenceLabel(evidenceConfidencePercent),
    evidenceConfidencePercent,
    applicability,
    recommendations,
    unansweredMaterialityCount,
    notSureCount,
  }

  return {
    ...partial,
    leadQualification: buildLeadQualification(state, partial),
  }
}

export function isQuestionVisible(
  questionId: string,
  state: AssessmentState,
  showWhen?: Array<{ questionId: string; equals?: string | string[]; notEquals?: string | string[] }>,
): boolean {
  if (!showWhen?.length) return true
  return showWhen.every((rule) => {
    const value = state.responses[rule.questionId]
    const asString = Array.isArray(value) ? value.join(',') : String(value ?? '')
    if (rule.equals != null) {
      const expected = Array.isArray(rule.equals) ? rule.equals : [rule.equals]
      if (Array.isArray(value)) return expected.some((e) => value.includes(e))
      return expected.includes(asString)
    }
    if (rule.notEquals != null) {
      const blocked = Array.isArray(rule.notEquals) ? rule.notEquals : [rule.notEquals]
      if (Array.isArray(value)) return !blocked.some((e) => value.includes(e))
      return !blocked.includes(asString)
    }
    return true
  })
}
